import { deserializeMemory, Register, serializeMemory } from '@/Emulator/Utility';

export class Cartridge implements ISerializable
{
    public readonly id: string;
    public readonly mapperId: number;
    public readonly mirrorMode: MirrorMode;
    public readonly videoSystem: VideoSystem;
    public readonly prgRom: Uint8Array;
    public readonly prgRam: Uint8Array;
    public readonly chrData: Uint8Array;
    public readonly hasBattery: boolean;
    public readonly hasChrRam: boolean;
    public readonly apuTiming: ApuTiming;

    private readonly data: Uint8Array;

    constructor(buffer: ArrayBuffer)
    {
        this.data = new Uint8Array(buffer);
        this.id   = this.generateId();

        if (!this.isValidNesFile(buffer)) {
            throw new Error('The given ROM data is not a valid NES cartridge.');
        }

        const prgRomSizeUnits: number = this.data[4]; // number of 16kb units.
        const chrRomSizeUnits: number = this.data[5]; // number of 8kb units.
        const prgRamSize: number      = this.data[8]; // number of 8kb units.

        const flags6  = Register.make8(this.data, 6);
        const flags7  = Register.make8(this.data, 7);
        const flags9  = Register.make8(this.data, 9);
        const flags12 = Register.make8(this.data, 12);

        const isVerticalMirroring: boolean = flags6.hasBit(0);
        const hasBattery: boolean          = flags6.hasBit(1);
        const hasTrainer: boolean          = flags6.hasBit(2);
        const isFourScreen: boolean        = flags6.hasBit(3);
        const isPal: boolean               = (flags7.value & 0x0C) === 0x08
            ? (flags12.value & 0x03) === 1
            : flags9.hasBit(0);

        const trainerSize: number  = hasTrainer ? 512 : 0;
        const prgRomOffset: number = 16 + trainerSize;
        const prgRomSize: number   = prgRomSizeUnits * 16384;
        const chrRomOffset: number = prgRomOffset + prgRomSize;
        const chrRomSize: number   = chrRomSizeUnits * 8192;

        this.mapperId   = (flags7.value & 0xF0) | ((flags6.value & 0xF0) >> 4);
        this.hasBattery = hasBattery;
        this.hasChrRam  = chrRomSizeUnits === 0;

        this.mirrorMode = isFourScreen
            ? MirrorMode.FourScreen
            : isVerticalMirroring
                ? MirrorMode.Vertical
                : MirrorMode.Horizontal;

        if (isPal) {
            this.videoSystem = {
                type:               VideoSystemType.Pal,
                fps:                24.4,
                cpuCyclesPerSecond: 1662607,
                ppuSpeedRatio:      3.2,
            };
            this.apuTiming   = {
                mode0Steps: [8313, 16627, 24939, 33252],
                mode1Steps: [8313, 16627, 24939, 33253, 41565],
            };
        } else {
            this.videoSystem = {
                type:               VideoSystemType.Ntsc,
                fps:                30,
                cpuCyclesPerSecond: 1789773,
                ppuSpeedRatio:      3.0,
            };
            this.apuTiming   = {
                mode0Steps: [7457, 14913, 22371, 29829],
                mode1Steps: [7457, 14913, 22371, 29830, 37281],
            };
        }

        this.prgRam = new Uint8Array(Math.max(1, prgRamSize) * 8192);
        this.prgRom = this.data.slice(prgRomOffset, prgRomOffset + prgRomSize);

        // If a cartridge claims to have CHR RAM, it is physically swapped out
        // on the hardware level from being a ROM chip to a RAM chip. In this
        // case, instead of carving out the CHR ROM, we allocate 8KB of memory.
        this.chrData = this.hasChrRam
            ? new Uint8Array(8192)
            : this.data.slice(chrRomOffset, chrRomOffset + chrRomSize);
    }

    /**
     * @inheritDoc
     */
    public serialize(): Record<string, any>
    {
        return {
            id:      this.id,
            prgRam:  serializeMemory(this.prgRam),
            chrData: this.hasChrRam ? serializeMemory(this.chrData) : null,
        };
    }

    /**
     * @inheritDoc
     */
    public deserialize(data: Record<string, any>): void
    {
        if (data.id !== this.id) {
            throw new Error(`Invalid save state for this cartridge!`);
        }

        this.prgRam.set(deserializeMemory(data.prgRam));

        if (data.chrData && this.hasChrRam) {
            this.chrData.set(deserializeMemory(data.chrData));
        }
    }

    /**
     * Checks whether the magic bytes in the header correspond to NES*.
     */
    private isValidNesFile(buffer: ArrayBuffer): boolean
    {
        const data: Uint8Array = new Uint8Array(buffer, 0, 4);

        return data[0] === 0x4E  // N
            && data[1] === 0x45  // E
            && data[2] === 0x53  // S
            && data[3] === 0x1A; // EOF
    }

    /**
     * Generates a fast, synchronous 32-bit FNV-1a hash of the ROM data
     * to uniquely identify it for save states and battery-backed RAM.
     */
    private generateId(): string
    {
        let hash: number = 0x811c9dc5; // FNV offset basis

        for (let i = 0; i < this.data.length; i++) {
            hash ^= this.data[i];
            // FNV prime multiplication, optimized for 32-bit JS integers
            hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        }

        // Convert to an unsigned 32-bit integer and then to a hex string
        return (hash >>> 0).toString(16);
    }
}

export type ApuTiming = {
    mode0Steps: [number, number, number, number];
    mode1Steps: [number, number, number, number, number];
};

export type VideoSystem = {
    type: VideoSystemType;
    fps: number;
    cpuCyclesPerSecond: number;
    ppuSpeedRatio: number;
}

export const VideoSystemType = {
    Pal:  'PAL',
    Ntsc: 'NTSC',
} as const;

export type VideoSystemType = typeof VideoSystemType[keyof typeof VideoSystemType];

export const MirrorMode = {
    Horizontal: 'HORIZONTAL',
    Vertical:   'VERTICAL',
    FourScreen: 'FOUR_SCREEN',
} as const;

export type MirrorMode = typeof MirrorMode[keyof typeof MirrorMode];
